import type { FC, SyntheticEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { CloseButton } from '@oneToOne/shared/components/buttons/CloseButton'
import { OtoButton } from '@oneToOne/shared/components/buttons/OtoButton'
import { Header } from '@oneToOne/shared/components/Header'
import { RESPONSE_CSAT } from '@oneToOne/shared/testIds'

import { Image } from '@core/components/Image'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

import {
  agentImage,
  agentImageContainer,
  closeButton,
  customerSurveyContent,
  customerSurveyWrapper,
  feedbackText,
  headerText,
  questionSubText,
  questionText,
  questionTextArea,
  successWrapper,
} from './CustomerSurvey.twind'
import { Rating } from './Rating'

export interface CustomerSurveyProps {
  avatarUrl: string | undefined
  fallbackAvatarUrl?: string
  onSubmit: (rating: number, feedback?: string) => void
  onClose: () => void
}

/**
 * Customer Survey (NPS) for customers that have entered a conversation and have subsequently ended it.
 */
export const CustomerSurvey: FC<CustomerSurveyProps> = ({
  avatarUrl,
  fallbackAvatarUrl,
  onSubmit,
  onClose,
}) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()

  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentRating, setCurrentRating] = useState<undefined | number>(
    undefined,
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isShowingSuccess, setIsShowingSuccess] = useState(false)

  useEffect(() => {
    if (isShowingSuccess) {
      successTimeoutRef.current = setTimeout(() => {
        onClose()
      }, 2400)
    }

    return () => {
      if (successTimeoutRef.current !== null) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [isShowingSuccess, onClose])

  function onRatingSubmit(rating: number) {
    if (currentRating !== rating) {
      setCurrentRating(rating)
      onSubmit(rating)
    }
  }

  function onFeedbackSubmit(e: SyntheticEvent) {
    e.preventDefault()
    if (!currentRating) {
      return
    }
    if (textareaRef.current?.value) {
      onSubmit(currentRating, textareaRef.current?.value)
    }
    setIsShowingSuccess(true)
  }

  return (
    <div className={tw?.(customerSurveyWrapper)} data-testid={RESPONSE_CSAT}>
      <Header
        rightActions={() => (
          <CloseButton variant="secondary" onClick={onClose} />
        )}
      />
      {isShowingSuccess ? (
        <div className={tw?.(successWrapper)}>
          <div className={tw?.(agentImageContainer)}>
            <Avatar src={avatarUrl} fallbackSrc={fallbackAvatarUrl} />
            <span className={tw?.(feedbackText, 'text-lg')}>
              {t(`Thank you for your feedback!`)}
            </span>
          </div>
          <OtoButton
            overrideCSS={closeButton}
            onClick={onClose}
            aria-label={t('Close')}
          >
            {t('Close')}
          </OtoButton>
        </div>
      ) : (
        <>
          <div
            className={tw?.(
              customerSurveyContent,
              'flex',
              'justify-start',
              'items-start',
              'flex-col',
              'w-full',
            )}
          >
            <div className={tw?.(agentImageContainer)}>
              <Avatar src={avatarUrl} fallbackSrc={fallbackAvatarUrl} />
            </div>
            <span
              className={tw?.(headerText, 'text-lg')}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
            >
              {t('How do you feel about your experience today?')}
            </span>
            <div
              className={tw?.(
                'flex',
                'justify-center',
                'items-center',
                'w-full',
              )}
            >
              <Rating currentRating={currentRating} onChange={onRatingSubmit} />
            </div>
            <div
              className={tw?.(
                'flex',
                'justify-start',
                'items-start',
                'flex-col',
                'w-full',
                'mt-12',
                'mb-3',
              )}
            >
              <span className={tw?.(questionText, 'text-sm')}>
                {t(`Is there anything else you want to tell us?`)}
              </span>
              <span className={tw?.(questionSubText, 'text-sm')}>
                {t(`(optional)`)}
              </span>
              <textarea
                className={tw?.(questionTextArea)}
                ref={textareaRef}
                placeholder={t(`Share your feedback`)}
              />
            </div>
          </div>
          <OtoButton
            overrideCSS={closeButton}
            onClick={onFeedbackSubmit}
            disabled={!currentRating}
            aria-label={t('Submit')}
          >
            {t('Submit')}
          </OtoButton>
        </>
      )}
    </div>
  )
}

const Avatar: FC<{
  src?: string | undefined
  fallbackSrc?: string | undefined
}> = ({ src, fallbackSrc }) => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  return (
    <>
      {src ? (
        <Image
          className={tw?.(agentImage)}
          src={src}
          alt={t(`Avatar picture`)}
          fallbackComponent={
            fallbackSrc ? (
              <Image
                className={tw?.(agentImage)}
                src={fallbackSrc}
                alt={t(`Avatar picture`)}
              />
            ) : null
          }
        />
      ) : fallbackSrc ? (
        <Image
          className={tw?.(agentImage)}
          src={fallbackSrc}
          alt={t(`Avatar picture`)}
        />
      ) : null}
    </>
  )
}
